import type { AbilityIncreasePlan } from '@rollcall/contracts'
import { increaseRemainder, type AbilityIncrease } from '@rollcall/rules'
import { useT } from './i18n-context.js'

/**
 * Прибавки предыстории (D23: их предлагает запись, а не код).
 *
 * И характеристики, и допустимые раскладки приходят с сервера:
 * «+2 и +1» либо «+1 всем трём» — это правило источника. Проверяет
 * раскладку сервер, замечание приходит на слот `increase`
 * и показывается здесь же.
 *
 * Ведёт себя как пул характеристик: взятая прибавка из списка
 * у остальных исчезает. Раньше здесь у каждой характеристики стоял
 * полный список величин, и «+2 всем трём» можно было выбрать, чтобы
 * получить отказ сервера. Механика одна — значит и поведение одно;
 * что именно осталось доступным, считает `packages/rules`, тем же
 * кодом, каким потом проверяет (CLAUDE.md, п. 5).
 */
export function IncreaseField({
  plan,
  increase,
  problems,
  onChange,
}: {
  readonly plan: AbilityIncreasePlan
  readonly increase: readonly AbilityIncrease[]
  readonly problems: readonly string[]
  readonly onChange: (increase: readonly AbilityIncrease[]) => void
}) {
  const t = useT()
  const invalid = problems.length > 0

  const amountOf = (ability: string) =>
    increase.find((item) => item.ability === ability)?.amount ?? 0

  /**
   * Что можно поставить этой характеристике: остаток от раскладок,
   * если не считать её собственную прибавку. Своя прибавка в остаток
   * входит — потому и вычитается из розданного, а не добавляется
   * к списку отдельным вариантом: иначе она стояла бы в нём дважды.
   */
  const availableFor = (ability: string) =>
    increaseRemainder(
      plan.patterns,
      increase.filter((item) => item.ability !== ability).map((item) => item.amount),
    )

  const left = increaseRemainder(
    plan.patterns,
    increase.map((item) => item.amount),
  )

  return (
    <fieldset
      className="app-slot"
      data-slot="increase"
      data-invalid={invalid ? 'true' : undefined}
    >
      <legend className="app-slot-legend">{t('wizard.increase.title')}</legend>
      <p className="app-slot-hint">
        {t('wizard.increase.hint', {
          patterns: plan.patterns.map((pattern) => pattern.join('+')).join(' / '),
        })}
      </p>

      <div className="app-abilities">
        {plan.abilities.map((ability) => {
          const chosen = amountOf(ability)
          const available = availableFor(ability)
          return (
            <label key={ability} className="app-field">
              <span>{t(`abilities.${ability}`)}</span>
              <select
                value={chosen}
                onChange={(event) => {
                  const amount = Number(event.target.value)
                  const rest = increase.filter((item) => item.ability !== ability)
                  onChange(amount === 0 ? rest : [...rest, { ability, amount }])
                }}
              >
                <option value={0}>{t('wizard.increase.none')}</option>
                {available.map((amount) => (
                  <option key={amount} value={amount}>
                    {`+${amount}`}
                  </option>
                ))}
              </select>
            </label>
          )
        })}
      </div>

      <p className="app-slot-hint" data-testid="increase-left">
        {left.length === 0
          ? t('wizard.increase.done')
          : t('wizard.increase.left', {
              values: left.map((amount) => `+${amount}`).join(', '),
            })}
      </p>

      {invalid && (
        <ul className="app-problems" role="alert">
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      )}
    </fieldset>
  )
}
